import React, { useEffect, useState } from 'react';
import { Button, Image, makeStyles, mergeClasses, Persona, Title2 } from '@fluentui/react-components';
import ICaseStudy from '../../../models/caseStudy/ICaseStudy';
import { useParams } from 'react-router-dom';
import caseStudyService from '../../../services/CaseStudyService';
import Hero from '../../../components/hero/Hero';
import ContactForm from '../../../components/contactForm/ContactForm';
import SEO from '../../../app/SEO';
import { useTranslation } from 'react-i18next';
import Status from '../../../enums/Status';
import ITestimonial from '../../../models/testimonial/ITestimonial';
import testimonialService from '../../../services/TestimonialService';
import { NIL } from 'uuid';
import Placeholder from '../../../components/pagePlaceholder/Placeholder';
import { dolphinTokens } from '../../../utils/Theme';
import { Open16Regular } from '@fluentui/react-icons';
import { useHeroMainSnap } from '../../../hooks/useHeroMainSnap';
import clientService from '../../../services/ClientService';
import IClient from '../../../models/client/IClient';

const useStyles = makeStyles({
    mainContainer: {
        padding: 0
    },
    container: {
        padding: "1rem 2rem",
        fontSize: "1.125rem",
        margin: "0 auto",
        maxWidth: "100%",

        "& > h2": {
            textAlign: "center",
        },

        "& > div": {
            width: "1200px",
            margin: "0 auto",
            lineHeight: 1.6,
            "@media (max-width: 1200px)": {
                width: "100%",
            }
        }
    },
    clientContainer: {
        backgroundColor: dolphinTokens.colorLightBlue20,
    },
    introductionContainer: {
        backgroundColor: "white",
    },
    briefContainer: {
        backgroundColor: dolphinTokens.colorSkyLavender20,
    },
    solutionContainer: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white"
    },
    testimonialContainer: {
        backgroundColor: dolphinTokens.colorDarkBlue,
        color: "white"
    },
    testimonialContent: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",

        "@media (max-width: 600px)": {
            flexDirection: "column",
        },

        "& > :first-child": {
            flexShrink: 0,
            width: "200px",

            "@media (max-width: 600px)": {
                width: "100%",
            }
        },

        "& > :last-child": {
            margin: "auto",
            fontSize: "1rem",
            display: "flex",

            "::before": {
                content: '"“"',
                display: "inline-block",
                marginRight: "0.5em",
                fontSize: "2rem",
            },

            "&::after": {
                content: '"”"',
                display: "inline-block",
                marginLeft: "0.5em",
                fontSize: "2rem",
            },
        },
    },
    persona: {
        margin: "1rem 0 !important",

        "& > span ": {
            color: "white"
        }
    },
    clientInformationContainer: {
        display: "flex",
        alignItems: "center",
    },
    clientLogo: {
        display: "block",
        marginRight: "1rem",
        textAlign: "center",

        "& img": {
            display: "block",
            width: "100%",
            maxWidth: "150px",
            height: "auto",
            maxHeight: "150px",
            margin: "0 auto",
            marginBottom: "1rem",
        }
    },
    contact: {
        width: '100%',
        backgroundColor: "white"
    }
});

const CaseStudy: React.FC = () => {
    const { t } = useTranslation(["casestudies", "general"]);
    const { slug } = useParams();
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [caseStudy, setCaseStudy] = useState<ICaseStudy>(null!);
    const [client, setClient] = useState<IClient>(null!);
    const [testimonial, setTestimonial] = useState<ITestimonial>(null!);

    useEffect(() => {
        fetchCaseStudy(slug!, setCaseStudy, setClient, setTestimonial, setStatus);
    }, [slug]);

    useHeroMainSnap("casestudy-hero", "casestudy-main", {
        duration: 900,
        easing: 'quart',
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    if (status === Status.LOADING) {
        return <Placeholder />;
    }

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    const jsonLD: {}[] = [
        {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "name": caseStudy.title,
            "author": {
                "@type": "Organization",
                "name": t("companyName", { ns: "general" }),
            },
            "datePublished": "2025-04-01",
            "description": caseStudy.metaDescription,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `https://www.consultdolphin.com/casestudy/${caseStudy.slug}`
            }
        }
    ];

    return (
        <div>
            <SEO
                title={{ title: caseStudy.title, pageTitle: caseStudy.title }}
                metaDescription={caseStudy.metaDescription}
                canonicalPath={`/casestudy/${caseStudy.slug}`}
                openGraph={{
                    title: caseStudy.title,
                    description: caseStudy.metaDescription,
                    image: caseStudy.image.url,
                    url: `/casestudy/${caseStudy.slug}`,
                }}
                jsonLD={jsonLD}
            />

            <div id="casestudy-hero">
                <Hero
                    title={caseStudy.title}
                    subtitle={caseStudy.subtitle}
                    imageUri={caseStudy.image.url}
                />
            </div>

            <div id="casestudy-main" className={styles.mainContainer}>
                <div className={mergeClasses(styles.container, styles.clientContainer)}>

                    <Title2 block as="h2">
                        {t("sections.client")}
                    </Title2>

                    <div className={styles.clientInformationContainer}>
                        <div className={styles.clientLogo}>
                            {client?.logo?.url &&
                                !client.logo.url.includes(".null") && (
                                    <Image
                                        src={client.logo.url}
                                        alt={client.name}
                                    />
                            )}
                            <a href={client.website} target="_blank" rel="noreferrer">
                                <Button type="button" appearance="secondary" icon={<Open16Regular />}>
                                    {t("clientInfo.website")}
                                </Button>
                            </a>
                        </div>
                        <ul>
                            <li dangerouslySetInnerHTML={{ __html: t("clientInfo.name", { name: client.name }) }} />
                            <li dangerouslySetInnerHTML={{ __html: t("clientInfo.industry", { industry: client.industry }) }} />
                            <li dangerouslySetInnerHTML={{ __html: t("clientInfo.size", { size: client.size }) }} />
                        </ul>
                    </div>
                </div>

                <div className={mergeClasses(styles.container, styles.introductionContainer)}>
                    <Title2 block as="h2">
                        {t("sections.intro")}
                    </Title2>
                    <div dangerouslySetInnerHTML={{ __html: caseStudy.introduction }} />
                </div>

                <div className={mergeClasses(styles.container, styles.briefContainer)}>
                    <Title2 block as="h2">
                        {t("sections.challenges")}
                    </Title2>
                    <div dangerouslySetInnerHTML={{ __html: caseStudy.brief }} />
                </div>

                <div className={mergeClasses(styles.container, styles.solutionContainer)}>
                    <Title2 block as="h2">
                        {t("sections.solution")}
                    </Title2>
                    <div dangerouslySetInnerHTML={{ __html: caseStudy.solution }} />
                </div>

                {
                    testimonial &&
                    <div className={mergeClasses(styles.container, styles.testimonialContainer)}>
                        <Title2 block as="h2">
                            {t("sections.testimonial")}
                        </Title2>

                        <div className={styles.testimonialContent}>
                            <Persona
                                size="huge"
                                className={styles.persona}
                                name={testimonial.contact.name}
                                secondaryText={testimonial.contact.position}
                                tertiaryText={new Date(testimonial.date).toLocaleDateString()}
                            />

                            <div dangerouslySetInnerHTML={{ __html: testimonial.content }} />
                        </div>
                    </div>
                }

                <div className={styles.contact}>
                    <ContactForm
                        title={t("contact.title")}
                        subtitle={t("contact.subtitle")}
                    />
                </div>

            </div>
        </div>
    );
};

async function fetchCaseStudy(
    slug: string,
    setCaseStudy: (data: ICaseStudy) => void,
    setClient: (data: IClient) => void,
    setTestimonial: (data: ITestimonial) => void,
    setStatus: (status: Status) => void
) {
    try {
        const result = await caseStudyService.getCaseStudy(slug);
        const client = await clientService.getClient(result.clientId);
        setClient(client);

        if (result.testimonialId && result.testimonialId !== NIL) {
            await fetchTestimonial(result.testimonialId, setTestimonial);
        }

        setCaseStudy(result);
        setStatus(Status.LOAD_SUCCESS);
    } catch {
        setStatus(Status.LOAD_ERROR);
    }
}

async function fetchTestimonial(
    id: string,
    setTestimonial: (data: ITestimonial) => void
) {
    try {
        const testimonial = await testimonialService.getTestimonial(id);
        setTestimonial(testimonial);
    } catch {
        setTestimonial(null!);
    }
}

export default CaseStudy;